// @ts-check
// doc-provenance-core.js — pure logic for the document provenance viewer:
// resolve a PDF source, reconstruct text-layer lines with boxes, and locate
// each parsed line item's source line. UI lives in doc-provenance.jsx; this
// file stays plain JS so scripts/test-provenance.js can exercise it in Node
// against the real PDFs in "test files/".

// ── session PDF cache ───────────────────────────────────────────────────────
// Uploaded PDF File objects can't be persisted to Firestore, and the live job
// listener re-emits docs without them right after every upload — so without a
// cache the provenance viewer would have to re-fetch the storage URL
// cross-origin (which needs a bucket CORS policy). Keyed by storage URL (or
// filename when the upload failed). MODULE-scoped, not component-scoped, so it
// survives InputView remounting when you switch job tabs — for the whole page
// session. Cleared only by a full reload (the genuine cross-session case).
const _sessionPdfFiles = new Map();
const _fileKeyOf = (d) => (d && (d._fileUrl || d._fileName)) || null;
function rememberLocalFile(doc) {
  const k = _fileKeyOf(doc);
  if (k && doc && doc._localFile) _sessionPdfFiles.set(k, doc._localFile);
}
function attachLocalFile(doc) {
  const k = _fileKeyOf(doc);
  return (doc && k && _sessionPdfFiles.has(k))
    ? { ...doc, _localFile: _sessionPdfFiles.get(k) }
    : doc;
}

// ── PDF source resolution ───────────────────────────────────────────────────
// Returns where the viewer can load the PDF from, or null when none is
// reachable (pasted text, spreadsheets, sample data, failed storage upload).
function pdfSourceOf(invoice) {
  if (!invoice) return null;
  const f = invoice._localFile;
  if (f && (f.type === 'application/pdf' || /\.pdf$/i.test(f.name || ''))) return { file: f };
  if (invoice._fileUrl && /\.pdf$/i.test(invoice._fileName || '')) return { url: invoice._fileUrl };
  return null;
}

// ── text-layer line reconstruction (with boxes) ─────────────────────────────
// Same rounded-Y grouping as extractPDFText (invoice-parser.js) so the lines
// here correspond 1:1 to the lines Claude parsed; additionally keeps page and
// the union bbox of every glyph run on the line (PDF user-space coordinates,
// origin bottom-left).
async function extractDocLines(pdf) {
  // Pages are independent, so fetch + parse them all concurrently instead of
  // awaiting one page before starting the next. Promise.all preserves array
  // order, so concatenating the per-page results keeps document (page) order.
  const perPage = await Promise.all(
    Array.from({ length: pdf.numPages }, (_, i) => i + 1).map(async (p) => {
      const page    = await pdf.getPage(p);
      const content = await page.getTextContent();
      const byY = {};
      for (const item of content.items) {
        if (!item.str || !item.str.trim()) continue;
        const y = Math.round(item.transform[5]);
        (byY[y] = byY[y] || []).push(item);
      }
      const lines = [];
      // The keys are rounded Y positions as strings; `b - a` coerces them.
      for (const y of /** @type {any[]} */ (Object.keys(byY)).sort((a, b) => b - a)) {
        const items = byY[y].sort((a, b) => a.transform[4] - b.transform[4]);
        const yBase = +y;
        const h     = Math.max(8, ...items.map((i) => i.height || 0));
        lines.push({
          page: p,
          text: items.map((i) => i.str).join(' '),
          x0: Math.min(...items.map((i) => i.transform[4])) - 2,
          x1: Math.max(...items.map((i) => i.transform[4] + (i.width || 0))) + 2,
          y0: yBase - 2,
          y1: yBase + h + 2,
        });
      }
      return lines;
    })
  );
  return perPage.flat();
}

// ── item → line locator ─────────────────────────────────────────────────────
// Claude rewrites descriptions slightly (inch symbol → "in", spacing), so an
// exact substring search won't work. Instead score every line:
//   + fraction of the item's description tokens present on the line
//   + 0.8 when the printed SKU appears (strongest signal)
//   + 0.2 per money/qty figure (qty, unit, amount) found on the line
// and take the best line above a threshold. Numbers disambiguate template-
// heavy vendors where many rows share most words ("BLK 150# ...").
function _locateItem(item, docLines) {
  const tokensOf = (s) => (s || '').toLowerCase()
    .replace(/["'#,]/g, ' ')
    .split(/\s+/).filter(Boolean);
  // "in"/"ft" are parser substitutions for ″/′ — they won't appear in the PDF.
  const descToks = tokensOf(item.desc).filter((t) => t !== 'in' && t !== 'ft');
  const sku      = (item.sku || '').trim().toLowerCase();
  const thousands = (s) => s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const numVariants = [item.qty, item.unit, item.amount]
    .filter((v) => typeof v === 'number' && v > 0)
    .map((v) => [...new Set([
      String(v), v.toFixed(2), thousands(v.toFixed(2)),
      v.toFixed(4).replace(/0+$/, '').replace(/\.$/, ''),
    ])]);
  // Numbers must match as standalone figures, not substrings — qty "4" must
  // hit the "4" in "EA 4 4 0" but never the "4" inside "$417.56".
  const esc    = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const hasNum = (text, v) => new RegExp(`(^|[^\\d.])${esc(v)}(?![\\d])`).test(text);
  const numSignals = (lower) => {
    let n = 0;
    for (const variants of numVariants) if (variants.some((v) => hasNum(lower, v))) n++;
    return n;
  };

  let best = null;
  docLines.forEach((ln, idx) => {
    const lower  = ln.text.toLowerCase();
    const lnToks = new Set(tokensOf(ln.text));
    const hits      = descToks.filter((t) => lnToks.has(t)).length;
    const descScore = descToks.length ? hits / descToks.length : 0;
    const skuHit    = !!sku && lower.includes(sku);
    const score     = descScore + (skuHit ? 0.8 : 0) + 0.2 * numSignals(lower);
    if (!best || score > best.score) best = { idx, score, descScore, skuHit };
  });
  // Accept on either anchor: the description matched, or the printed SKU did
  // (vendors like Porter put the SKU/qty/price row and the description on two
  // separate text-layer lines — the SKU row can win with descScore ≈ 0).
  if (!best || best.score < 0.8 || (best.descScore < 0.2 && !best.skuHit)) return null;

  // A printed item often spans adjacent text-layer lines (desc above/below
  // the numbers row, or a wrapped description). A neighbor is a candidate when
  // it clearly belongs to THIS item: it carries the SKU / two of the item's
  // figures, or it consists mostly of description tokens the best line lacks.
  // Each candidate gets a confidence score so locateAllItems can resolve
  // contention — a line wanted by two items goes to the stronger claim.
  const bestLn   = docLines[best.idx];
  const bestToks = new Set(tokensOf(bestLn.text));
  const leftover = descToks.filter((t) => !bestToks.has(t));
  // Dimension tokens like "1-1/2x6" / "2x4" pin the exact size. Stacked-up
  // sibling rows (a "1-1/4X6 XHVY BLK NIPPLE" line sitting right above a
  // "1-1/2X6 XHVY BLK NIPPLE" item) share every other word, so the
  // description-overlap test alone would bleed the highlight onto them — a
  // neighbor carrying a DIFFERENT size is a different item, not a continuation.
  const xSizesOf   = (s) => tokensOf(s).filter((t) => /\d/.test(t) && t.includes('x'));
  const itemXSizes = new Set(xSizesOf(item.desc));
  const conflictingSize = (text) =>
    itemXSizes.size > 0 && xSizesOf(text).some((s) => !itemXSizes.has(s));
  const neighbors = [];
  const consider = (idx) => {
    const ln = docLines[idx];
    if (!ln || ln.page !== bestLn.page) return;
    const lower = ln.text.toLowerCase();
    if (sku && lower.includes(sku))  return neighbors.push({ idx, conf: 3 });
    if (numSignals(lower) >= 2)      return neighbors.push({ idx, conf: 2 });
    const toks    = tokensOf(ln.text);
    const carried = toks.filter((t) => leftover.includes(t)).length;
    if (carried >= 2 && carried >= toks.length * 0.5 && !conflictingSize(ln.text)) {
      neighbors.push({ idx, conf: carried / toks.length });
    }
  };
  consider(best.idx - 1);
  consider(best.idx + 1);
  return { best: best.idx, neighbors };
}

function locateItemLines(item, docLines) {
  const r = _locateItem(item, docLines);
  return r ? [r.best, ...r.neighbors.map((n) => n.idx)].sort((a, b) => a - b) : [];
}

// ── whole-document locator with cross-item claiming ─────────────────────────
// Per-item location bleeds on template-heavy vendors: item 27's numbers row
// sits directly below item 26's description line, and that description shares
// most words ("CS WELD STD ... B16.9 A234 WPB"), so item 27 pulls it in as a
// "continuation". Locating every item together fixes this structurally: each
// text-layer line belongs to at most one item — best lines are claimed first,
// then contested neighbor lines go to the strongest claim (own-description
// carries a higher token fraction than a sibling's template overlap).
function locateAllItems(items, docLines) {
  const located = (items || []).map((it) => _locateItem(it, docLines));
  const claimed = new Set(located.flatMap((r) => (r ? [r.best] : [])));
  const out     = located.map((r) => (r ? [r.best] : []));
  const cands   = [];
  located.forEach((r, item) => {
    if (r) r.neighbors.forEach((n) => cands.push({ item, ...n }));
  });
  cands.sort((a, b) => b.conf - a.conf);
  for (const c of cands) {
    if (claimed.has(c.idx)) continue;
    claimed.add(c.idx);
    out[c.item].push(c.idx);
  }
  return out.map((arr) => arr.sort((a, b) => a - b));
}

// ── row → source documents ──────────────────────────────────────────────────
// Items inside comparison rows are matcher clones — recover their index in the
// source document so the cached whole-document locations can be looked up.
function itemIndexIn(doc, item) {
  if (typeof item._qIndex === 'number') return item._qIndex;
  const m = /::(\d+)$/.exec(item._id || '');
  if (m) return +m[1];
  return (doc.items || []).findIndex((it) =>
    it.desc === item.desc && it.qty === item.qty && (it.sku || '') === (item.sku || ''));
}

// Every screen that shows a comparison row wants the same affordance: open the
// original quote / invoice PDF at the exact line this row came from. This is the
// one mapping from a row to its source documents, so the four screens that offer
// it can't drift apart.
//
// Rows come from aggregateInvoices: `quoteItem` is the quote line (absent on
// off-quote "added" rows) and `breakdown[]` carries one entry per invoice line
// that drew against it (absent on "missing" rows, never invoiced). Screens with
// a different row shape adapt to this one — see pairAsRow in match-audit.jsx.
//
// Only documents with a reachable PDF are returned, so a caller can treat an
// empty result as "nothing to show" without re-checking.
function rowPreviewSources(row, quote, invoices) {
  if (!row) return [];
  const out = [];
  if (row.quoteItem && quote) {
    const doc = attachLocalFile(quote);
    if (pdfSourceOf(doc)) {
      out.push({ key: 'q', label: 'View on quote', doc, sel: itemIndexIn(quote, row.quoteItem) });
    }
  }
  if (row.breakdown && row.breakdown.length && invoices) {
    const byId = new Map(invoices.map((iv) => [iv.id, iv]));
    // A row can draw from the same invoice twice (two lines of the same material
    // on one document). Both get an entry, so `key` carries the ordinal —
    // keying on invoiceId alone collided and React dropped one of them.
    row.breakdown.forEach((b, i) => {
      if (!b.srcItem) return;
      const inv = byId.get(b.invoiceId);
      if (!inv) return;
      const doc = attachLocalFile(inv);
      if (!pdfSourceOf(doc)) return;
      out.push({
        key: `${b.invoiceId}-${i}`,
        label: `View on invoice ${b.invoiceId}`,
        doc,
        sel: itemIndexIn(inv, b.srcItem),
      });
    });
  }
  return out;
}

// A triage list shows one action row per open item, so it wants ONE affordance,
// not a row of them. For an exception the document worth opening is almost
// always the INVOICE — the thing that was actually billed, and the thing the
// vendor will be asked about. A row that was never invoiced (quoted but missing
// from every invoice) has only the quote line, so that is the fallback.
// Returns null when neither document has a reachable PDF.
function primaryPreviewSource(row, quote, invoices) {
  const all = rowPreviewSources(row, quote, invoices);
  return all.find((x) => x.key !== 'q') || all[0] || null;
}

export {
  pdfSourceOf, extractDocLines, locateItemLines, locateAllItems,
  rememberLocalFile, attachLocalFile,
  itemIndexIn, rowPreviewSources, primaryPreviewSource,
};
